import { CheckIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';

import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { InquiryDraftFormData } from '@/hooks/useInquiryForm';
import { segmentClient } from '@/util/analytics';

interface AnonymousCheckboxProps {
  control: Control<InquiryDraftFormData>;
  errors: FieldErrors<InquiryDraftFormData>;
  isDisabled?: boolean;
}

export function AnonymousCheckbox({ control, errors, isDisabled = false }: AnonymousCheckboxProps) {
  return (
    <Controller
      name="anonymous"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControl isInvalid={!!errors.anonymous?.message} isDisabled={isDisabled}>
          <VStack className="rounded-md bg-background-50 p-3">
            <Checkbox
              value={value?.toString() || ''}
              onChange={(checked) => {
                onChange(checked);

                segmentClient.track(ANALYTICS_EVENTS.ANONYMOUS_INQUIRY_TOGGLED, {
                  enabled: checked,
                });
              }}
              isChecked={value}
              size="sm"
              isDisabled={isDisabled}
              aria-label="Submit anonymously"
              testID="anonymous-checkbox">
              <CheckboxIndicator>
                <CheckboxIcon as={CheckIcon} />
              </CheckboxIndicator>
              <CheckboxLabel>
                <>Submit anonymously</>
              </CheckboxLabel>
            </Checkbox>
          </VStack>

          <Text size="xs" className="mt-2 font-normal text-typography-500">
            Experts will see your case details but not your name or institution.
          </Text>

          {errors.anonymous && (
            <FormControlError className="mt-2">
              <FormControlErrorIcon />
              <FormControlErrorText size="xs">{errors.anonymous.message}</FormControlErrorText>
            </FormControlError>
          )}
        </FormControl>
      )}
    />
  );
}
